<template>
  <div
    role="document"
    class="t-tower t-xmlviewer"
    tabindex="0"
    @focus="moveFocusToVisiblePage"
    ref="viewer"
  >
    <div
      v-for="page in pages"
      :key="page.pageNum"
      v-observe-visibility="isVisible => setOnScreen(page.pageNum, isVisible)"
      :ref="`page${page.pageNum}`"
      :style="page.pageNum === lastPage && {minHeight: '100vh'}"
      tabindex="-1"
    >
      <XmlPage
        v-bind="page"
        :shouldKeep="shouldKeep(page.pageNum, onScreenBounds)"
        :shouldLoad="shouldLoad(page.pageNum, onScreenBounds)"
        :isCurrentPage="page.pageNum === pageNum"
      />
      <PageBreak v-if="page.pageNum !== lastPage" />
    </div>
  </div>
</template>

<style scoped>
.t-tower {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  /*
   * To pass accessibility testing, a vertically-scrolling application cannot
   * also scroll horizontally, even when zoomed. Depending on the
   * customer-supplied content and CSS, the content might be wider than the
   * tower's container, but there's nothing we can do about that. Just hide the
   * horizontal scrollbar and hope for the best.
   */
  overflow-x: hidden;
  overflow-y: auto;
}
</style>

<script>
import debounce from 'lodash/debounce'
import config from '../config'
import PageBreak from './PageBreak.vue'
import XmlPage from './XmlPage.vue'

export default {
  name: 'Tower',

  inheritAttrs: false,

  props: {
    firstPage: {
      type: Number,
      required: true,
    },
    lastPage: {
      type: Number,
      required: true,
    },
    pages: {
      type: Array,
      required: true,
    },
    pageNum: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      contentZoom:
        parseFloat(
          window
            .getComputedStyle?.(document.documentElement)
            .getPropertyValue('--zoom-content'),
        ) || 1,
      onScreen: {},
    }
  },

  computed: {
    onScreenBounds() {
      const osks = Object.keys(this.onScreen)
        .map(s => parseInt(s))
        .filter(Boolean)
        .sort()
      return [osks[0], osks[osks.length - 1]]
    },

    scrollPageNum() {
      return this.onScreenBounds[0]
    },
  },

  watch: {
    contentZoom() {
      const scrollTop = this.$refs.viewer?.scrollTop
      let firstVisible, offsetTop
      if (scrollTop !== undefined) {
        for (const el of this.$refs.viewer.getElementsByTagName('*')) {
          if (el.offsetTop >= scrollTop) {
            firstVisible = el
            offsetTop = el.offsetTop
            break
          }
        }
      }

      document.documentElement.style.setProperty(
        '--zoom-content',
        this.contentZoom,
      )

      if (firstVisible) {
        this.$refs.viewer.scrollTop =
          firstVisible.offsetTop * (scrollTop / offsetTop)
      }
    },

    pageNum() {
      const {pageNum, scrollPageNum} = this
      if (scrollPageNum && pageNum !== scrollPageNum) {
        this.scrollTo(pageNum, true)
      }
    },

    scrollPageNum() {
      this.syncPageNum()
    },
  },

  mounted() {
    this.scrollTo(this.pageNum, false)
  },

  methods: {
    getPageWrapperDiv(pageNum) {
      const div = this.$refs[`page${pageNum}`]
      return div && div[0] // array because inside v-for
    },

    moveFocusToVisiblePage() {
      const div = this.getPageWrapperDiv(this.scrollPageNum)
      if (div) {
        div.focus()
      }
    },

    scrollTo(pageNum, shouldFocus) {
      const div = this.getPageWrapperDiv(pageNum)
      if (div) {
        div.scrollIntoView()
        // Racy HACK
        this.$nextTick(() => {
          div.scrollIntoView()
          shouldFocus && div.focus()
        })
      }
    },

    shouldKeep(pageNum, [firstOnScreen, lastOnScreen]) {
      return (
        !!firstOnScreen &&
        pageNum >= firstOnScreen - config.keepBackward &&
        pageNum <= lastOnScreen + config.keepForward
      )
    },

    shouldLoad(pageNum, [firstOnScreen, lastOnScreen]) {
      return (
        !!firstOnScreen &&
        pageNum >= firstOnScreen - config.prefetchBackward &&
        pageNum <= lastOnScreen + config.prefetchForward
      )
    },

    setOnScreen(pageNum, isVisible) {
      if (isVisible && !this.onScreen[pageNum]) {
        this.$set(this.onScreen, pageNum, true)
      } else if (this.onScreen[pageNum] && !isVisible) {
        this.$delete(this.onScreen, pageNum)
      } else {
        return
      }
    },

    syncPageNum: debounce(function () {
      if (this.pageNum !== this.scrollPageNum) {
        this.setPageNum(this.scrollPageNum, {method: 'replace'})
      }
    }, 500),

    zoomIn() {
      this.contentZoom += 0.2
    },

    zoomOut() {
      this.contentZoom -= 0.2
    },

    setZoom(zoom) {
      this.contentZoom = zoom
    },
  },

  inject: ['setPage', 'setPageNum'],

  components: {
    PageBreak,
    XmlPage,
  },
}
</script>
